<template>
  <v-container fluid class="pa-6">
    <!-- 错误提示 -->
    <div v-if="error" class="mb-4 text-red-500" style="text-align: center;margin: 0 auto;">
      {{ error }}
    </div>

    <!-- 加载提示 -->
    <div v-if="loading" class="flex justify-center p-4" style="text-align: center;margin: 0 auto;">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>

    <!-- 图纸展示区域 -->
    <div class="pb-4">
      <div v-if="!loading && drawsData.length === 0" class="text-center py-8">
        <img 
          src="@/assets/noimage.png" 
          alt="暂无图纸" 
          class="mx-auto no-image"
        />
      </div>

      <DrawingList
        v-else-if="!loading && drawsData.length > 0"
        :drawings="drawsData"
        :selected-states="selectedStates"
        :image-dimensions="[]"
        :show-actions="false"
        @image-click="handleImageClick"
        @toggle-select="handleToggleSelect"
      />
    </div>

     <!-- CAD文件显示区域 -->
     <div class="pb-4">
      <CadFileList
        :files="dwgsData"
        :loading="loading"
        mode='view'
        @download="downloadDwg"
        @view="viewDwg" 
      />
    </div>

    <!-- 底部固定按钮栏 -->
    <div class="bottom-actions">
      <v-btn 
        :disabled="!Object.values(selectedStates).some(checked => checked)"
        prepend-icon="mdi-printer"
        @click="handlePrintClick(true)"
        class="print-button"
      >
        打印选中({{ selectedCount }})
      </v-btn>
      <v-btn 
        :disabled="drawsData.length === 0"
        prepend-icon="mdi-printer-outline"
        @click="handlePrintClick(false)"
        class="print-all-button"
      >
        打印全部({{ drawsData.length }})
      </v-btn>
      <v-btn 
        :disabled="drawsData.length === 0"
        variant="flat"
        prepend-icon="mdi-file-pdf-box"
        @click="handleExportPDFClick"
        class="export-pdf-button"
      >
        导出PDF({{ selectedCount > 0 ? selectedCount : drawsData.length }})
      </v-btn>
    </div>

    <!-- 图片预览对话框 -->
    <ImagePreviewDialog
      v-model="showPreview"
      :image-url="previewImage"
      @close="handleClosePreview"
    />

    <!-- 导出PDF对话框 -->
    <ExportPDFDialog
      v-model="exportPDFDialogOpen"
      :has-images="drawsData.length > 0"
      :has-selected-images="selectedCount > 0"
      :total-count="drawsData.length"
      :selected-count="selectedCount"
      @export="handleExportPDF"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DrawingList from '@/components/DrawingList.vue'
import ImagePreviewDialog from '@/components/ImagePreviewDialog.vue'
import ExportPDFDialog from '@/components/ExportPDFDialog.vue'
import { orderApi, uploadApi } from '@/services/api'
import { handlePrint } from '@/utils/print'
import { useOrderStore } from '@/stores/order'
import CadFileList from '@/components/CadFileList.vue'
const router = useRouter()
const route = useRoute()

// 状态
const loading = ref(false)
const error = ref<string | null>(null)
const drawsData = ref<string[]>([])
const dwgsData = ref<string[]>([])
const selectedStates = ref<Record<number, boolean>>({})
const uploadDialogOpen = ref(false)
const uploadMode = ref<'new' | string>('new')
const uploading = ref(false)
const uploadProgress = ref(0)
const uploadFiles = ref<File[] | null>(null)
const showPreview = ref(false)
const previewImage = ref('')
const deleteConfirmOpen = ref(false)
const deleteIndex = ref(-1)
const exportPDFDialogOpen = ref(false)
const orderData = ref({})
const orderStore = useOrderStore()
// 计算属性
const contractId = computed(() => route.params.contractId)
const selectedCount = computed(() => 
  Object.values(selectedStates.value).filter(checked => checked).length
)

// 获取数据
const fetchData = async () => {
  if (!contractId.value) {
    error.value = '订单ID不能为空'
    return
  }
  
  loading.value = true
  error.value = null
  
  try {
    console.log('开始获取图纸列表...')
    if (!orderStore.orderData) {
      await orderStore.fetchOrderData(Number(contractId.value))
    }
    const orderData = orderStore.orderData 
  
      try {
        if (orderData.drawsAll) { 
          drawsData.value = JSON.parse(orderData.drawsAll) 
          dwgsData.value = await uploadApi.getFilesByUrls(JSON.parse(orderStore.orderData.dwgsHeader))
        } else {
          drawsData.value = []
          console.warn('未找到图纸数据')
        }
      } catch (parseError) {
        console.error('解析图纸数据失败:', parseError)
        error.value = '解析图纸数据失败'
        drawsData.value = []
      }
    
    
    // 初始化选中状态
    selectedStates.value = {}
    drawsData.value.forEach((_, index) => {
      selectedStates.value[index] = false
    })
    
    console.log('图纸数据处理完成:', {
      total: drawsData.value.length,
      data: drawsData.value
    })
  } catch (err: any) {
    console.error('获取图纸列表失败:', err)
    error.value = err.message || '获取图纸列表失败，请刷新页面重试'
    drawsData.value = []
  } finally {
    loading.value = false
  }
}

// 获取订单数据
const fetchOrderData = async () => {
  const contractId = route.params.contractId
  if (!contractId) return

  try {
    const response = await orderApi.getDetail(Number(contractId), 0)
    if (response.code === 0 && response.data) {
      console.log('订单数据:', response.data)
      orderData.value = response.data
    }
  } catch (error) {
    console.error('获取订单数据失败:', error)
  }
}

// 监听路由变化
watch(() => route.params.contractId, fetchOrderData, { immediate: true })

// 处理图片选择
const handleToggleSelect = (index: number, checked: boolean) => {
  selectedStates.value[index] = checked
}

// 处理单击选中
const handleImageClick = (index: number) => {
  previewImage.value = drawsData.value[index]
  showPreview.value = true
}

// 关闭预览
const handleClosePreview = () => {
  showPreview.value = false
  previewImage.value = ''
}

// 处理打印
const handlePrintClick = (selectedOnly: boolean) => {
  const imagesToPrint = selectedOnly
    ? drawsData.value.filter((_, index) => selectedStates.value[index])
    : drawsData.value

  handlePrint(imagesToPrint)
}

// 处理导出PDF
const handleExportPDFClick = () => {
  exportPDFDialogOpen.value = true
}

const handleExportPDF = (selectedOnly: boolean, orientation: 'portrait' | 'landscape') => {
  console.log('导出PDF', { selectedOnly, orientation });
  const imagesToExport = selectedOnly
    ? drawsData.value.filter((_, index) => selectedStates.value[index])
    : drawsData.value;
  console.log('导出图片', imagesToExport);
  
  // 获取合同编号作为文件名
  const fileName = orderData.value?.so || '图纸导出';
  console.log('文件名:', fileName, orderData.value);
  handlePrint(imagesToExport, true, orientation, fileName);
}


// 查看CAD文件
const viewDwg = (file: any) => {
  console.log('查看CAD文件', file)
  window.open('http://cad2.jiangfengoa.com?dwg='+file.url, '_blank')
}
 

// 下载CAD文件
const downloadDwg = (file: any) => {
  console.log('下载CAD文件', file)
  // 设置下载文件名
  // 使用fetch获取文件内容并创建blob下载
  fetch(file.url)
    .then(res => res.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.originName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    })
    .catch(err => {
      console.error('下载失败:', err)
    })
}

// 生命周期
onMounted(() => {
  if (!localStorage.getItem('token')) {
    router.push('/login')
    return
  }
  
  fetchData()
  fetchOrderData()
})
</script>

<style scoped>
.bottom-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  background-color: rgba(43, 45, 51, 0.7);
  backdrop-filter: blur(10px);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.print-button,
.print-all-button,
.export-pdf-button {
  background: #E34C65 !important;
  color: white !important;
  min-width: 120px;
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
}

/* 为底部按钮区域留出空间 */
.pb-4 {
  padding-bottom: 80px;
}

.no-image {
  width: 300px;
  height: auto;
  opacity: 0.5;
}
</style>